import { useState, useRef, useEffect } from 'react';
import { authService } from '../../services/authService';
import { MfaState } from '../../types/auth';
import { useThemeStore } from '../../store/themeStore';

interface LoginPanelProps {
  onLoginSuccess: () => void;
}

const LoginPanel = ({ onLoginSuccess }: LoginPanelProps) => {
  const theme = useThemeStore(state => state.theme);
  const logoSrc = theme === 'dark' ? '/assets/gridpilot_logo_dark.svg' : '/assets/gridpilot_logo.svg';
  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // MFA state
  const [mfaState, setMfaState] = useState<MfaState>(authService.getMfaState());
  const [mfaMethod, setMfaMethod] = useState<'pin' | 'totp'>('pin');
  const [pin, setPin] = useState('');
  const [totp, setTotp] = useState('');
  const [mfaError, setMfaError] = useState<string | null>(null);

  // Refs for auto-focus
  const pinInputRef = useRef<HTMLInputElement>(null);
  const totpInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus MFA input when MFA is required
  useEffect(() => {
    if (mfaState.required) {
      const defaultMethod = mfaState.availableMethods[0] || 'pin';
      setMfaMethod(defaultMethod);

      setTimeout(() => {
        if (defaultMethod === 'pin' && pinInputRef.current) {
          pinInputRef.current.focus();
        } else if (defaultMethod === 'totp' && totpInputRef.current) {
          totpInputRef.current.focus();
        }
      }, 100);
    }
  }, [mfaState.required, mfaState.availableMethods]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await authService.login(username, password);

      if (result.success) {
        onLoginSuccess();
      } else if (result.requiresMfa) {
        const newMfaState = authService.getMfaState();
        setMfaState(newMfaState);
      } else {
        setError(result.error || 'Anmeldung fehlgeschlagen');
      }
    } catch (err) {
      console.error('[LoginPanel] Error:', err);
      setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaError(null);
    setIsLoading(true);

    try {
      let result;
      if (mfaMethod === 'pin') {
        if (!pin || pin.length !== 6 || !/^\d{6}$/.test(pin)) {
          setMfaError('Bitte eine 6-stellige PIN eingeben');
          setIsLoading(false);
          return;
        }
        result = await authService.completeMfaPin(pin);
      } else {
        if (!totp || !/^\d{6}$/.test(totp)) {
          setMfaError('Bitte den 6-stelligen Authenticator-Code eingeben');
          setIsLoading(false);
          return;
        }
        result = await authService.completeMfaTotp(totp);
      }

      if (result.success) {
        onLoginSuccess();
      } else {
        setMfaError(result.error || 'MFA-Verifizierung fehlgeschlagen');
      }
    } catch {
      setMfaError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelMfa = () => {
    authService.clearMfaState();
    setMfaState(authService.getMfaState());
    setPin('');
    setTotp('');
    setMfaError(null);
  };

  const validPin = /^\d{6}$/.test(pin);
  const validTotp = /^\d{6}$/.test(totp);

  // Render MFA form
  if (mfaState.required) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-primary-900">
        <div className="max-w-md w-full p-8 bg-white dark:bg-primary-800 rounded-lg shadow-lg">
          <div className="flex justify-center mb-6">
            <img src={logoSrc} alt="GridPilot" className="h-10 w-auto" />
          </div>

          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              Zwei-Faktor-Authentifizierung
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm">
              Bitte geben Sie Ihren Sicherheitscode ein
            </p>
          </div>

          {/* MFA Method Tabs */}
          {mfaState.availableMethods.length > 1 && (
            <div className="flex gap-2 justify-center mb-4">
              {mfaState.availableMethods.includes('pin') && (
                <button
                  type="button"
                  onClick={() => setMfaMethod('pin')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    mfaMethod === 'pin'
                      ? 'bg-accent-primary text-white'
                      : 'bg-gray-200 dark:bg-primary-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  PIN
                </button>
              )}
              {mfaState.availableMethods.includes('totp') && (
                <button
                  type="button"
                  onClick={() => setMfaMethod('totp')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    mfaMethod === 'totp'
                      ? 'bg-accent-primary text-white'
                      : 'bg-gray-200 dark:bg-primary-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  Authenticator
                </button>
              )}
            </div>
          )}

          {mfaError && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded text-sm">
              {mfaError}
            </div>
          )}

          <form onSubmit={handleMfaSubmit} className="space-y-4">
            {mfaMethod === 'pin' ? (
              <div>
                <label htmlFor="pin" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sicherheits-PIN (6 Ziffern)
                </label>
                <input
                  ref={pinInputRef}
                  id="pin"
                  type="password"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="------"
                  disabled={isLoading}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-primary-600 rounded-md shadow-sm bg-white dark:bg-primary-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-accent-primary focus:border-accent-primary text-center text-2xl tracking-widest disabled:bg-gray-100"
                />
              </div>
            ) : (
              <div>
                <label htmlFor="totp" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Authenticator-Code (6 Ziffern)
                </label>
                <input
                  ref={totpInputRef}
                  id="totp"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  value={totp}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  disabled={isLoading}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-primary-600 rounded-md shadow-sm bg-white dark:bg-primary-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-accent-primary focus:border-accent-primary text-center text-2xl tracking-widest disabled:bg-gray-100"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCancelMfa}
                disabled={isLoading}
                className="flex-1 py-2 px-4 border border-gray-300 dark:border-primary-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-primary-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={isLoading || (mfaMethod === 'pin' ? !validPin : !validTotp)}
                className="flex-1 py-2 px-4 rounded-md text-white bg-accent-primary hover:bg-accent-primary-hover disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Prüfe...' : 'Bestätigen'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Render login form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-primary-900">
      <div className="max-w-md w-full p-8 bg-white dark:bg-primary-800 rounded-lg shadow-lg">
        <div className="flex justify-center mb-8">
          <img src={logoSrc} alt="GridPilot" className="h-10 w-auto" />
        </div>

        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Anmeldung</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Bitte melden Sie sich an
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Benutzername
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              disabled={isLoading}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-primary-600 rounded-md shadow-sm bg-white dark:bg-primary-700 text-gray-900 dark:text-white focus:outline-hidden focus:ring-accent-primary focus:border-accent-primary disabled:bg-gray-100 placeholder-gray-400"
              autoComplete="username"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Passwort
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-primary-600 rounded-md shadow-sm bg-white dark:bg-primary-700 text-gray-900 dark:text-white focus:outline-hidden focus:ring-accent-primary focus:border-accent-primary disabled:bg-gray-100 placeholder-gray-400"
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent-primary hover:bg-accent-primary-hover focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Anmeldung läuft...' : 'Anmelden'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginPanel;
